import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { StorageLocationArticle } from '@knust/api-interfaces';
import { StorageLocationArticlesStore } from '../../utility/signal-stores/storage-location-articles.store';
import { PAGE_SIZE_OPTIONS } from '../../utility/signal-stores/pagination';
import { AuthService } from '../../utility/services/auth.service';
import { NotificationService } from '../../utility/services/notification.service';
import { DEFAULT_IMAGE } from '../../utility/constants/app.constants';
import { calculateNextIntervalStep } from '../../utility/functions/change-to-next-interval-step.function';
import {
  addItemsToCart,
  addItemsToCartSuccess,
} from '../../trunks/+state/trunk.actions';

const BASE_COLUMNS = ['photo', 'internalId', 'title', 'actualStock', 'reservedStock'];
const MANAGEMENT_COLUMNS = ['autoOrderLessStock', 'notificationStock', 'targetStock', 'storageStatus'];

export const STORAGE_STATUS_LABELS: Record<number, string> = {
  1: 'Ausverkauft',
  2: 'Kein eiserner Bestand hinterlegt',
  3: 'Bestand ausreichend',
  4: 'Meldebestand erreicht',
  5: 'Eiserner Bestand erreicht',
};

@Component({
  selector: 'knust-storage-location-articles',
  imports: [
    MatTableModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressBarModule,
    RouterLink,
  ],
  providers: [StorageLocationArticlesStore],
  templateUrl: './storage-location-articles.component.html',
  styleUrl: './storage-location-articles.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StorageLocationArticlesComponent {
  protected readonly store = inject(StorageLocationArticlesStore);
  readonly #router = inject(Router);
  readonly #ngrxStore = inject(Store);
  readonly #notificationService = inject(NotificationService);
  readonly #authService = inject(AuthService);

  protected readonly defaultImage = DEFAULT_IMAGE;
  protected readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  protected readonly storageStatusLabels = STORAGE_STATUS_LABELS;
  protected readonly displayedColumns = [
    ...BASE_COLUMNS,
    ...(this.#authService.isAdmin() || this.#authService.isPurchasing() ? MANAGEMENT_COLUMNS : []),
    'buyStock',
  ];

  protected readonly searchTerm = signal('');

  // Gewünschte Mengen je Lagerort-Artikel. Bleiben beim Blättern und Suchen erhalten.
  protected readonly amounts = signal<Record<number, number>>({});
  protected readonly selectedCount = computed(
    () => Object.values(this.amounts()).filter((amount) => amount > 0).length,
  );

  readonly #storageLocationId = toSignal(
    inject(ActivatedRoute).paramMap.pipe(
      map((paramMap) => Number(paramMap.get('storageLocationId'))),
    ),
  );

  constructor() {
    effect(() => {
      const storageLocationId = this.#storageLocationId();

      if (storageLocationId) {
        this.searchTerm.set('');
        this.amounts.set({});
        this.store.setStorageLocation(storageLocationId);
      }
    });

    inject(Actions)
      .pipe(ofType(addItemsToCartSuccess), takeUntilDestroyed())
      .subscribe(() => {
        this.amounts.set({});
        // Reservierte Bestände haben sich geändert
        this.store.reload();
      });
  }

  protected search(value: string) {
    this.searchTerm.set(value);
    this.store.setSearch(value);
  }

  protected changePage(event: PageEvent) {
    this.store.setPage(event.pageIndex, event.pageSize);
  }

  protected openArticle(row: StorageLocationArticle) {
    if (row.article?.id) {
      this.#router.navigate(['/artikel', row.article.id]);
    }
  }

  protected setAmount(row: StorageLocationArticle, input: HTMLInputElement) {
    if (row.id === undefined) {
      return;
    }

    const amount = normalizeAmount(
      input.valueAsNumber,
      row.article?.samplingStep ?? 1,
      row.actualStock,
    );
    const rowId = row.id;

    // Das Feld zeigt sofort den korrigierten Wert, auch wenn sich das Signal nicht ändert
    input.value = amount ? String(amount) : '';

    this.amounts.update((amounts) => {
      const next = { ...amounts };

      if (amount) {
        next[rowId] = amount;
      } else {
        delete next[rowId];
      }

      return next;
    });
  }

  protected addToCart() {
    const items = Object.entries(this.amounts())
      .filter(([, amount]) => amount > 0)
      .map(([storageLocationArticleId, amount]) => ({
        storageLocationArticleId: Number(storageLocationArticleId),
        amount,
      }));

    if (items.length === 0) {
      this.#notificationService.createNotification({
        title: 'Kein Artikel ausgewählt',
        text: 'Es muss mindestens eine Einheit von einem Artikel ausgewählt werden.',
        status: 'error',
        dismissed: 0,
      });
      return;
    }

    this.#ngrxStore.dispatch(addItemsToCart({ items }));
  }
}

/**
 * Rundet eine Eingabe auf den nächsten Entnahmeschritt auf, wie in knust-article-storage-locations.
 * Zusätzlich wird die Menge auf den größten Entnahmeschritt begrenzt, der noch verfügbar ist.
 */
export function normalizeAmount(
  value: number,
  samplingStep: number,
  availableStock: number,
): number | null {
  const step = samplingStep > 0 ? samplingStep : 1;

  if (!Number.isFinite(value) || value <= 0) {
    return null;
  }

  const amount = Math.min(
    calculateNextIntervalStep(value, step),
    Math.floor(availableStock / step) * step,
  );

  return amount > 0 ? amount : null;
}
